(() => {
    "use strict";

    const SELECTOR = "[data-home-work-slider]";
    const SWIPE_THRESHOLD = 48;

    function bindSlider(slider) {
        if (slider.dataset.bound === "true") {
            return;
        }

        slider.dataset.bound = "true";

        const track = slider.querySelector("[data-home-work-track]");
        const slides = Array.from(slider.querySelectorAll("[data-home-work-slide]"));
        const dots = Array.from(slider.querySelectorAll("[data-home-work-dot]"));
        const previous = slider.querySelector("[data-home-work-prev]");
        const next = slider.querySelector("[data-home-work-next]");
        const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const interval = Number.parseInt(slider.dataset.homeWorkInterval || "0", 10);
        let activeIndex = 0;
        let autoplay = null;
        let pointerStartX = null;
        let pointerDeltaX = 0;

        if (!track || slides.length === 0) {
            return;
        }

        const stopAutoplay = () => {
            if (!autoplay) {
                return;
            }

            window.clearInterval(autoplay);
            autoplay = null;
        };

        const startAutoplay = () => {
            stopAutoplay();

            if (reducedMotion || slides.length < 2 || !interval) {
                return;
            }

            autoplay = window.setInterval(() => activate(activeIndex + 1), interval);
        };

        const pauseSlideVideo = (slide) => {
            const iframe = slide.querySelector("iframe");
            if (!iframe) {
                return;
            }
            window.sitePauseYoutubeIframe?.(iframe);
            if (iframe.hasAttribute("data-youtube-src")) {
                iframe.removeAttribute("src");
            }
        };

        const loadSlideVideo = (slide) => {
            const iframe = slide.querySelector("iframe[data-youtube-src]");
            if (!iframe) {
                return;
            }
            const src = iframe.getAttribute("data-youtube-src") || "";
            if (src !== "" && iframe.getAttribute("src") !== src) {
                iframe.src = src;
            }
        };

        const activate = (requestedIndex, focusDot = false) => {
            activeIndex = (requestedIndex + slides.length) % slides.length;
            track.style.setProperty("--home-work-index", String(activeIndex));

            slides.forEach((slide, index) => {
                const isActive = index === activeIndex;
                slide.classList.toggle("is-active", isActive);
                slide.setAttribute("aria-hidden", String(!isActive));
                slide.toggleAttribute("inert", !isActive);
                if (isActive && slider.dataset.inView === "true") {
                    loadSlideVideo(slide);
                } else {
                    pauseSlideVideo(slide);
                }
            });

            dots.forEach((dot, index) => {
                const isActive = index === activeIndex;
                dot.classList.toggle("is-active", isActive);
                dot.setAttribute("aria-selected", String(isActive));
                dot.tabIndex = isActive ? 0 : -1;
            });

            if (focusDot) {
                dots[activeIndex]?.focus();
            }
        };

        previous?.addEventListener("click", () => {
            stopAutoplay();
            activate(activeIndex - 1);
            startAutoplay();
        });

        next?.addEventListener("click", () => {
            stopAutoplay();
            activate(activeIndex + 1);
            startAutoplay();
        });

        dots.forEach((dot, index) => {
            dot.addEventListener("click", () => {
                stopAutoplay();
                activate(index);
                startAutoplay();
            });

            dot.addEventListener("keydown", (event) => {
                if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
                    return;
                }

                event.preventDefault();
                stopAutoplay();
                activate(activeIndex + (event.key === "ArrowRight" ? 1 : -1), true);
                startAutoplay();
            });
        });

        slider.addEventListener("keydown", (event) => {
            if (event.target.closest("button, a, input, select, textarea")) {
                return;
            }

            if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
                return;
            }

            event.preventDefault();
            stopAutoplay();
            activate(activeIndex + (event.key === "ArrowRight" ? 1 : -1));
            startAutoplay();
        });

        slider.addEventListener("pointerdown", (event) => {
            if (event.pointerType === "mouse" || slides.length < 2) {
                return;
            }

            pointerStartX = event.clientX;
            pointerDeltaX = 0;
            stopAutoplay();
            slider.setPointerCapture?.(event.pointerId);
        });

        slider.addEventListener("pointermove", (event) => {
            if (pointerStartX === null) {
                return;
            }

            pointerDeltaX = event.clientX - pointerStartX;
        });

        slider.addEventListener("pointerup", () => {
            if (pointerStartX === null) {
                return;
            }

            if (Math.abs(pointerDeltaX) >= SWIPE_THRESHOLD) {
                activate(activeIndex + (pointerDeltaX < 0 ? 1 : -1));
            }

            pointerStartX = null;
            pointerDeltaX = 0;
            startAutoplay();
        });

        slider.addEventListener("pointercancel", () => {
            pointerStartX = null;
            pointerDeltaX = 0;
            startAutoplay();
        });

        slider.addEventListener("mouseenter", stopAutoplay);
        slider.addEventListener("mouseleave", startAutoplay);
        slider.addEventListener("focusin", stopAutoplay);
        slider.addEventListener("focusout", (event) => {
            if (!slider.contains(event.relatedTarget)) {
                startAutoplay();
            }
        });

        const handleVisibilityChange = () => {
            if (!slider.isConnected) {
                stopAutoplay();
                document.removeEventListener("visibilitychange", handleVisibilityChange);
                return;
            }

            if (document.hidden) {
                stopAutoplay();
            } else {
                startAutoplay();
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        const observeVisibility = () => {
            if (!("IntersectionObserver" in window)) {
                slider.dataset.inView = "true";
                activate(0);
                startAutoplay();
                return;
            }

            const observer = new IntersectionObserver((entries) => {
                const visible = entries.some((entry) => entry.isIntersecting);
                slider.dataset.inView = visible ? "true" : "false";
                if (visible) {
                    loadSlideVideo(slides[activeIndex]);
                    startAutoplay();
                    return;
                }
                stopAutoplay();
                slides.forEach(pauseSlideVideo);
            }, { rootMargin: "80px 0px", threshold: 0.15 });

            observer.observe(slider);
            activate(0);
        };

        observeVisibility();
    }

    function bindOrcamentoSteps(list) {
        if (list.dataset.bound === "true") {
            return;
        }

        list.dataset.bound = "true";

        const cards = Array.from(list.querySelectorAll("details.home-orcamento-step-card"));

        cards.forEach((card) => {
            card.addEventListener("toggle", () => {
                if (!card.open) {
                    return;
                }

                cards.forEach((other) => {
                    if (other !== card) {
                        other.open = false;
                    }
                });
            });
        });
    }

    function boot() {
        document.querySelectorAll(SELECTOR).forEach(bindSlider);
        document.querySelectorAll("[data-home-orcamento-steps]").forEach(bindOrcamentoSteps);
    }

    window.siteReady(boot);
})();
